import { useCallback, useMemo, useSyncExternalStore } from "react";

/**
 * Tutorials each user has been shown, per browser, so the announcement shows
 * once. localStorage, like dismissed announcements: it needs no backend.
 */
const key = (uid: string) => `caseblink.tutorialsSeen.v1.${uid}`;

/** Tutorials put off with Later; they badge the account menu until opened. */
const laterKey = (uid: string) => `caseblink.tutorialsLater.v1.${uid}`;
const LATER_EVENT = "caseblink:tutorials-later";

const readIds = (storageKey: string): string[] => {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
};

const writeIds = (storageKey: string, ids: string[]): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(ids));
  } catch {
    // Private mode: it shows again next visit.
  }
};

export const readSeenTutorials = (uid: string): string[] => readIds(key(uid));

export const markTutorialsSeen = (uid: string, ids: string[]): void =>
  writeIds(key(uid), Array.from(new Set([...readSeenTutorials(uid), ...ids])));

export const readTutorialsLater = (uid: string): string[] =>
  readIds(laterKey(uid));

export const markTutorialsLater = (uid: string, ids: string[]): void => {
  writeIds(
    laterKey(uid),
    Array.from(new Set([...readTutorialsLater(uid), ...ids]))
  );
  window.dispatchEvent(new Event(LATER_EVENT));
};

export const clearTutorialsLater = (uid: string): void => {
  if (!readTutorialsLater(uid).length) return;
  writeIds(laterKey(uid), []);
  window.dispatchEvent(new Event(LATER_EVENT));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(LATER_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(LATER_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
};

/** The tutorials `uid` put off with Later, kept live across the app. */
export const useTutorialsLater = (uid: string): string[] => {
  const snapshot = useCallback(() => {
    try {
      return uid ? localStorage.getItem(laterKey(uid)) ?? "" : "";
    } catch {
      return "";
    }
  }, [uid]);
  const raw = useSyncExternalStore(subscribe, snapshot);
  return useMemo(() => (raw && uid ? readTutorialsLater(uid) : []), [raw, uid]);
};
